/* eslint-disable */
import React, { useState, useEffect } from "react";
import { toast } from "react-hot-toast";
import {
  X,
  Smartphone,
  Loader2,
  CheckCircle2
} from "lucide-react";
import api from "../services/api";

const FreePlanModal = ({ isOpen, onClose, userPhone }) => {
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [resendTimer, setResendTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  useEffect(() => {
    if (isOpen === false) {
      setOtp(["", "", "", "", "", ""]);
      setOtpSent(false);
      setVerified(false);
      setResendTimer(60);
      setCanResend(false);
    }
  }, [isOpen]);

  // Timer effect for resend button
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0 && !canResend && otpSent) {
      interval = setInterval(() => {
        setResendTimer(timer => {
          if (timer <= 1) {
            setCanResend(true);
            return 0;
          }
          return timer - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer, canResend, otpSent]);

  const handleChange = (element, index) => {
    if (isNaN(element.value)) return false;

    setOtp([...otp.map((d, idx) => (idx === index ? element.value : d))]);

    if (element.value && element.nextSibling) {
      element.nextSibling.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (otp[index]) {
        setOtp([...otp.map((d, idx) => (idx === index ? "" : d))]);
      }
      else if (index > 0) {
        const prevInput = e.target.previousElementSibling;
        if (prevInput) {
          prevInput.focus();
          setOtp([...otp.map((d, idx) => (idx === index - 1 ? "" : d))]);
        }
      }
    }
    else if (e.key === 'ArrowLeft' && index > 0) {
      e.target.previousElementSibling?.focus();
    }
    else if (e.key === 'ArrowRight' && index < 5) {
      e.target.nextElementSibling?.focus();
    }
  };

  const handleSendOTP = async () => {
    setLoading(true);
    try {
      const formdata = {
        phone_number: userPhone,
        company_id: localStorage.getItem('company_id'),
        user_id: localStorage.getItem('user_id'),
      };
      const response = await api.sendOptToPhone(formdata);
      if (response.status === 'success') {
        setOtpSent(true);
        setCanResend(false);
        setResendTimer(60);
        toast.success("OTP sent to your registered number!");
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Failed to send OTP. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();

    const otpValue = otp.join("");
    if (otpValue.length !== 6) {
      toast.error("Please enter a valid 6-digit code");
      return;
    }

    setLoading(true);
    try {
      const formdata = {
        phone_number: userPhone,
        company_id: localStorage.getItem('company_id'),
        user_id: localStorage.getItem('user_id'),
        otp: otpValue
      };

      const response = await api.verifyPhoneNumber(formdata);
      if (response.status === 'success') {
        setVerified(true);
        toast.success("Phone number verified successfully!");
        const savedContent = localStorage.getItem("content");
        let redirectUrl = response.data?.frontend_url; // e.g. the long login URL

        if (savedContent && redirectUrl) {
          try {
            const url = new URL(redirectUrl);
            const userParam = url.searchParams.get("user");
            if (userParam) {
              let userData = JSON.parse(decodeURIComponent(userParam));
              userData.route = `/dashboard/ai-agents/agent?content=${savedContent}`;

              const updatedUserData = encodeURIComponent(JSON.stringify(userData));
              url.searchParams.set("user", updatedUserData);
              redirectUrl = url.toString();
            }
          } catch (err) {
            console.error("Error updating redirectUrl:", err);
          }
        }
        localStorage.removeItem("content");
        setTimeout(() => {
          window.location.href = redirectUrl;
        }, 1000);
      } else {
        toast.error(response.message);
      }

    } catch (error) {
      toast.error("Invalid OTP. Please try again.");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setOtp(["", "", "", "", "", ""]);
    setCanResend(false);
    setResendTimer(60);
    await handleSendOTP();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={20} />
        </button>

        {verified ? (
          <div className="text-center space-y-4">
            <CheckCircle2 className="h-16 w-16 text-green-500 mx-auto" />
            <h2 className="text-xl font-semibold">
              Verification Successful!
            </h2>
            <p className="text-gray-600">
              Your Free plan is now active. Redirecting...
            </p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <Smartphone className="h-12 w-12 text-blue-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">
                Verify Your Phone Number
              </h2>
              <p className="text-gray-600">
                {!otpSent
                  ? "To activate your Free plan, we need to verify your phone number."
                  : "Enter the 6-digit code sent to your registered number"
                }
              </p>
            </div>

            {!otpSent ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
                  <div className="flex items-center gap-2">
                    <Smartphone className="h-5 w-5 text-blue-600" />
                    <span className="text-blue-800">
                      SMS will be sent to: {userPhone || "+1234567890"}
                    </span>
                  </div>
                </div>

                <button
                  onClick={handleSendOTP}
                  disabled={loading}
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center gap-2"
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin" />
                  ) : (
                    <>
                      <Smartphone size={16} />
                      Send Verification Code
                    </>
                  )}
                </button>
              </div>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-6">
                <div>
                  <div className="flex justify-center gap-2 mb-4">
                    {otp.map((data, index) => {
                      return (
                        <input
                          key={index}
                          type="text"
                          maxLength="1"
                          value={data}
                          className="w-10 h-10 border rounded-lg text-center text-lg font-medium focus:border-blue-500 focus:outline-none"
                          onChange={e => handleChange(e.target, index)}
                          onKeyDown={e => handleKeyDown(e, index)}
                          onFocus={e => e.target.select()}
                        />
                      );
                    })}
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg flex items-center justify-center"
                  disabled={loading || otp.join("").length !== 6}
                >
                  {loading ? (
                    <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  ) : null}
                  Verify & Activate Free Plan
                </button>

                <div className="text-center">
                  {canResend ? (
                    <button
                      type="button"
                      onClick={handleResendOTP}
                      className="text-blue-600 hover:underline text-sm"
                      disabled={loading}
                    >
                      Didn't receive the code? Resend
                    </button>
                  ) : (
                    <div className="text-gray-500 text-sm">
                      Resend code in {resendTimer} seconds
                    </div>
                  )}
                </div>
              </form>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default FreePlanModal; 